import { Route, useLocation, Routes } from 'react-router-dom';
import { Suspense, useEffect } from 'react';
import NotFound from './pages/NotFound';
import Forbidden from './pages/Forbidden';
import LoadingPage from './components/global/LoadingPage';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Layout from './layout/Layout';

// Storefront Pages
import Home from './pages/storefront/Home';
import About from './pages/storefront/About';
import Contact from './pages/storefront/Contact';
import PageBySlug from './pages/storefront/PageBySlug';
import Blog from './pages/storefront/Blog';
import PostDetailPage from './pages/storefront/PostDetailPage';
import Search from './pages/storefront/Search';
import ProductDetail from './pages/storefront/ProductDetail';

// Authenticated Routes
import Profile from './pages/profile/Profile';
import Security from './pages/customer/Security';
import CheckoutAccessRoute from './components/auth/CheckoutAccessRoute';
import NotificationPreferences from './pages/customer/NotificationPreferences';
import Returns from './pages/customer/Returns';
import ReturnDetail from './pages/customer/ReturnDetail';
import ReturnRequest from './pages/customer/ReturnRequest';
import Orders from './pages/orders/Orders';
import Checkout from './pages/checkout/Checkout';
import PaymentResult from './pages/checkout/PaymentResult';
import Wishlist from './pages/wishlist/Wishlist';
import Cart from './pages/cart/Cart';
import Addresses from './pages/customer/Addresses';
import PhoneUpdate from './pages/auth/PhoneUpdate';
import EmailUpdate from './pages/auth/EmailUpdate';

// Pages
const RoutesPath = () => {
    const ScrollToTop = () => {
        const { pathname } = useLocation();
        useEffect(() => {
            window.scrollTo(0, 0);
        }, [pathname]);
        return null;
    };

    return (
        <>
            <ScrollToTop />
            <Suspense fallback={<LoadingPage />}>
                <Routes>

                    {/* Public Storefront Routes */}
                    <Route path="/" element={<Layout />}>
                        {/* Auth Routes */}
                        <Route path="login" element={<Login />} />
                        <Route path="register" element={<Register />} />
                        <Route path="forgot-password" element={<ForgotPassword />} />

                        {/* 403 Forbidden Page */}
                        <Route path="/forbidden" element={<Forbidden />} />
                        {/* Home */}
                        <Route index element={<Home />} />

                        {/* Search & Products */}
                        <Route path="search" element={<Search />} />
                        <Route path="products/:slug" element={<ProductDetail />} />

                        {/* Static Pages */}
                        <Route path="about" element={<About />} />
                        <Route path="contact" element={<Contact />} />

                        {/* Blog Routes */}
                        <Route path="blog" element={<Blog />} />
                        <Route path="blog/:slug" element={<PostDetailPage />} />

                        {/* Dynamic Pages by Slug */}
                        <Route path="page/:slug" element={<PageBySlug />} />


                        {/* Protected Routes for authenticated users */}
                        <Route path="profile" element={
                            <ProtectedRoute>
                                <Profile />
                            </ProtectedRoute>
                        } />
                        <Route path="profile/update-phone" element={
                            <ProtectedRoute>
                                <PhoneUpdate />
                            </ProtectedRoute>
                        } />
                        <Route path="profile/update-email" element={
                            <ProtectedRoute>
                                <EmailUpdate />
                            </ProtectedRoute>
                        } />
                        <Route path="account/security" element={
                            <ProtectedRoute>
                                <Security />
                            </ProtectedRoute>
                        } />
                        <Route path="account/notifications" element={
                            <ProtectedRoute>
                                <NotificationPreferences />
                            </ProtectedRoute>
                        } />
                        <Route path="account/returns" element={
                            <ProtectedRoute>
                                <Returns />
                            </ProtectedRoute>
                        } />
                        <Route path="account/returns/:id" element={
                            <ProtectedRoute>
                                <ReturnDetail />
                            </ProtectedRoute>
                        } />
                        <Route path="account/orders/:id/return" element={
                            <ProtectedRoute>
                                <ReturnRequest />
                            </ProtectedRoute>
                        } />
                        {/* Cart works for guests via X-Cart-Session — no auth gate. */}
                        <Route path="cart" element={<Cart />} />
                        <Route path="orders" element={
                            <ProtectedRoute>
                                <Orders />
                            </ProtectedRoute>
                        } />
                        <Route path="checkout" element={
                            <CheckoutAccessRoute>
                                <Checkout />
                            </CheckoutAccessRoute>
                        } />
                        <Route path="checkout/:orderId/result" element={
                            <CheckoutAccessRoute>
                                <PaymentResult />
                            </CheckoutAccessRoute>
                        } />
                        <Route path="wishlist" element={
                            <ProtectedRoute>
                                <Wishlist />
                            </ProtectedRoute>
                        } />
                        <Route path="addresses" element={
                            <ProtectedRoute>
                                <Addresses />
                            </ProtectedRoute>
                        } />
                    </Route>

                    {/* 404 Not Found */}
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </Suspense>
        </>
    );
};

export default RoutesPath;